import type { NavigationConfig } from '~~/shared/types/navigation'

/**
 * Navigation configuration composable
 *
 * Provides navigation data for the mega menu system.
 * Currently returns static data, but structured to easily swap to API fetch later.
 *
 * Structure:
 * - 6 top-level items: Connectivity, Security, Managed IT, Industries, Support, About Us
 * - Each item has: Hook (left), Products (middle - up to 3 columns), Actions (right)
 */
export function useNavigation(): NavigationConfig {
  // Header phone follows the page region (a getter so client-side navigation
  // between regions stays reactive); see app/config/localNumbers.ts.
  const { display, tel } = useLocalPhone()
  return {
    megaMenuItems: [
      // ============================================================
      // CONNECTIVITY (3 product columns)
      // ============================================================
      {
        id: 'connectivity',
        label: 'Connectivity',
        hook: {
          label: 'Connectivity',
          headline: 'BUSINESS-GRADE INTERNET & VOICE',
          description:
            'Fast, reliable connections with local support. From nbn® to enterprise fibre and unified communications, one provider, one bill.',
        },
        products: [
          {
            id: 'business-internet',
            title: 'Business Internet',
            items: [
              {
                label: 'Business NBN',
                href: '/internet-data/business-nbn',
                icon: 'lucide:globe',
                description: 'Business-grade internet over the nbn® network',
              },
              {
                label: 'Fibre Internet',
                href: '/internet-data/fibre',
                icon: 'lucide:cable',
                description: 'Dedicated fibre with symmetric speeds',
              },
              {
                label: 'Enterprise Ethernet',
                href: '/internet-data/enterprise-ethernet',
                icon: 'lucide:zap',
                description: 'Guaranteed performance for mission-critical services',
              },
              {
                label: 'Wireless Ethernet',
                href: '/internet-data/wireless-ethernet',
                icon: 'lucide:radio-tower',
                description: 'Carrier-grade wireless for challenging locations',
              },
              {
                label: 'Private IP Network',
                href: '/internet-data/private-ip-network',
                icon: 'lucide:network',
                description: 'Secure private connectivity between business sites',
              },
              {
                label: 'IP Transit',
                href: '/internet-data/ip-transit',
                icon: 'lucide:arrow-right-left',
                description: 'High-capacity internet connectivity for service providers',
              },
            ],
          },
          {
            id: 'voice-comms',
            title: 'Voice & Unified Comms',
            items: [
              {
                label: 'VoIP Phone Systems',
                href: '/voice-video/voip-phone',
                icon: 'lucide:phone',
                description: 'Cloud-based phone systems for businesses',
              },
              {
                label: 'Microsoft Teams Voice',
                href: '/voice-video/microsoft-teams-voice',
                icon: 'lucide:message-square',
                description: 'Business calling integrated directly into Teams',
              },
              {
                label: 'Cloud PABX',
                href: '/voice-video/cloud-pabx',
                icon: 'lucide:phone-forwarded',
                description: 'Hosted phone system with advanced call features',
              },
              {
                label: 'SIP Voice',
                href: '/voice-video/sip-voice',
                icon: 'lucide:phone-call',
                description: 'SIP trunks for existing phone systems',
              },
              {
                label: 'Call Centre (CC4Teams)',
                href: '/voice-video/contact-centre-for-teams',
                icon: 'lucide:headset',
                description: 'Contact centre capabilities built for Microsoft Teams',
              },
              {
                label: 'Video Conferencing',
                href: '/voice-video/videoconferencing',
                icon: 'lucide:video',
                description: 'Meeting room and collaboration video solutions',
              },
              {
                label: '1300/1800 Numbers',
                href: '/voice-video/number-hosting',
                icon: 'lucide:hash',
                description: 'Inbound number hosting and call routing',
              },
            ],
          },
          {
            id: 'radio-infra',
            title: 'Radio & Infrastructure',
            items: [
              {
                label: 'Structured Cabling',
                href: '/project-services/structured-cabling-solutions',
                icon: 'lucide:cable',
                description: 'Commercial data and communications cabling',
              },
              {
                label: 'WiFi Solutions',
                href: '/project-services/wifi-solutions',
                icon: 'lucide:wifi',
                description: 'Managed wireless networks for business environments',
              },
              {
                label: 'Push to Talk Radio',
                href: '/radio-services/push-to-talk',
                icon: 'lucide:radio',
                description: 'Instant voice communication for mobile teams',
              },
              {
                label: 'Radio System Design',
                href: '/radio-services/radio-systems-design',
                icon: 'lucide:pencil-ruler',
                description: 'Design and planning of radio networks',
              },
              {
                label: 'Radio Maintenance',
                href: '/radio-services/deploy-maintain',
                icon: 'lucide:wrench',
                description: 'Deployment, maintenance, and ongoing radio support',
              },
            ],
          },
        ],

        actions: {
          title: 'I need to...',
          links: [
            { label: 'Connect multiple sites', href: '/contact?need=multi-site' },
            { label: 'Fix slow office WiFi', href: '/contact?need=wifi' },
            { label: 'Set up a new office', href: '/contact?need=new-office' },
            { label: 'Get backup connectivity', href: '/contact?need=backup' },
            { label: 'Upgrade office broadband', href: '/contact?need=upgrade' },
          ],
        },
      },

      // ============================================================
      // SECURITY (3 product columns)
      // ============================================================
      {
        id: 'security',
        label: 'Security',
        hook: {
          label: 'Security',
          headline: 'PROTECT YOUR PREMISES & PEOPLE',
          description:
            'Licensed security solutions with 24/7 professional monitoring. Comprehensive protection for Australian businesses.',
          link: {
            label: 'Service Areas',
            href: '/security-services/service-areas',
            icon: 'lucide:map-pin',
            description: 'Security services offered in your area',
          },
          badge: 'NSW Lic #000107431 • QLD Lic #4910062',
        },
        products: [
          {
            id: 'surveillance-access',
            title: 'Surveillance & Access',
            items: [
              {
                label: 'Commercial CCTV',
                href: '/security-services/cctv',
                icon: 'lucide:video',
                description: 'Live video monitoring and recording',
              },
              {
                label: 'Residential CCTV',
                href: '/security-services/residential-cctv',
                icon: 'lucide:home',
                description: 'Home camera systems with professional installation',
              },
              {
                label: 'Access Control',
                href: '/security-services/access-control',
                icon: 'lucide:door-open',
                description: 'Cards, fobs, and mobile access',
              },
              {
                label: 'Intercom Solutions',
                href: '/security-services/intercom-solutions',
                icon: 'lucide:volume-2',
                description: 'Audio and video entry systems',
              },
              {
                label: 'Residential Intercom Solutions',
                href: '/security-services/residential-intercom-solutions',
                icon: 'lucide:home',
                description: 'Home video and gate entry intercoms',
              },
              {
                label: 'Commercial Security',
                href: '/security-services/commercial-security-solutions',
                icon: 'lucide:shield',
                description: 'Integrated security for business premises',
              },
            ],
          },
          {
            id: 'monitoring-safety',
            title: 'Monitoring & Safety',
            items: [
              {
                label: '24/7 Alarm Monitoring',
                href: '/security-services/back-to-base-monitoring-24-7',
                icon: 'lucide:bell-ring',
                description: 'Back-to-base monitoring and response',
              },
              {
                label: 'Residential Alarm Monitoring',
                href: '/security-services/residential-alarm-monitoring',
                icon: 'lucide:home',
                description: 'Back-to-base monitoring for your home alarm',
              },
              {
                label: 'Alarm Systems',
                href: '/security-services/alarm-systems',
                icon: 'lucide:siren',
                description: 'Intrusion detection and alerts',
              },
              {
                label: 'Residential Alarm Systems',
                href: '/security-services/residential-alarm-systems',
                icon: 'lucide:home',
                description: 'Home alarm systems with professional installation',
              },
              {
                label: 'Lone Worker Safety',
                href: '/security-services/lone-worker-solutions',
                icon: 'lucide:hard-hat',
                description: 'Safety support for lone workers',
              },
              {
                label: 'Victims of Crime Aid',
                href: '/financial-assistance-for-victims-of-crime',
                icon: 'lucide:heart-handshake',
                description: 'Assistance with victims of crime funding',
              },
            ],
          },
          {
            id: 'by-industry',
            title: 'By Industry',
            items: [
              {
                label: 'Warehouses',
                href: '/industry-security-solutions/warehouse-security',
                icon: 'lucide:warehouse',
                description: 'Security for logistics and storage facilities',
              },
              {
                label: 'Retail & Shops',
                href: '/industry-security-solutions/retail-security',
                icon: 'lucide:shopping-bag',
                description: 'Theft deterrence and store monitoring',
              },
              {
                label: 'Childcare & Education',
                href: '/industry-security-solutions/childcare-education',
                icon: 'lucide:graduation-cap',
                description: 'Security for learning environments',
              },
              {
                label: 'School Security',
                href: '/industry-security-solutions/school-security-systems',
                icon: 'lucide:school',
                description: 'CCTV, access, intercoms and cabling for schools',
              },
              {
                label: 'Gyms & Fitness',
                href: '/industry-security-solutions/gym-fitness',
                icon: 'lucide:dumbbell',
                description: 'Secure access and facility monitoring',
              },
              {
                label: 'Construction Sites',
                href: '/industry-security-solutions/jobsite-surveillance-system',
                icon: 'lucide:hard-hat',
                description: 'Temporary monitoring for active sites',
              },
              {
                label: 'Property Management',
                href: '/industry-security-solutions/property-management',
                icon: 'lucide:building-2',
                description: 'Security for managed properties',
              },
            ],
          },
        ],

        actions: {
          title: 'I need to...',
          links: [
            { label: 'Secure my warehouse', href: '/contact?need=warehouse-security' },
            { label: 'Monitor multiple sites', href: '/contact?need=multi-site-security' },
            { label: 'Upgrade old cameras', href: '/contact?need=cctv-upgrade' },
            { label: 'Protect staff working alone', href: '/contact?need=lone-worker' },
          ],
          featured: {
            label: 'Victim of Crime?',
            href: '/financial-assistance-for-victims-of-crime',
            description: 'Get help with FACS funding',
          },
        },
      },

      // ============================================================
      // MANAGED IT & CLOUD (3 product columns)
      // ============================================================
      {
        id: 'managed-it',
        label: 'Managed IT & Cloud',
        hook: {
          label: 'Managed IT & Cloud',
          headline: 'SIMPLIFY YOUR IT OPERATIONS',
          description:
            '100% Australian-based support team. Proactive monitoring, strategic guidance, and hands-on expertise for growing businesses.',
        },
        products: [
          {
            id: 'it-support',
            title: 'Managed IT Support',
            items: [
              {
                label: 'AU Service Desk',
                href: '/managed-it-services/au-service-desk',
                icon: 'lucide:headphones',
                description: 'Australian-based helpdesk support for businesses',
              },
              {
                label: 'Online Security',
                href: '/managed-it-services/online-and-system-security',
                icon: 'lucide:shield-check',
                description: 'Protection against cyber threats and system risks',
              },
              {
                label: 'Hardware Asset Mgmt',
                href: '/managed-it-services/hardware-asset-management',
                icon: 'lucide:laptop',
                description: 'Track, manage, and maintain IT hardware assets',
              },
              {
                label: 'Remote Monitoring',
                href: '/managed-it-services/remote-monitoring-management',
                icon: 'lucide:monitor',
                description: 'Proactive monitoring of systems and devices',
              },
              {
                label: 'IT Strategy',
                href: '/managed-it-services/strategies-roadmaps-and-insights',
                icon: 'lucide:map',
                description: 'Technology planning aligned to business objectives',
              },
              {
                label: 'Integrated ICT Projects',
                href: '/managed-it-services/proactive-ict-projects',
                icon: 'lucide:puzzle',
                description: 'Planning and delivery of complex ICT projects',
              },
            ],
          },
          {
            id: 'cloud-services',
            title: 'Cloud Services',
            items: [
              {
                label: 'Cloud Backup',
                href: '/cloud-services/cloud-backup',
                icon: 'lucide:cloud-upload',
                description: 'Automated backup and secure data retention',
              },
              {
                label: 'Disaster Recovery',
                href: '/cloud-services/cloud-disaster-recovery',
                icon: 'lucide:refresh-cw',
                description: 'Systems recovery and business continuity solutions',
              },
              {
                label: 'Private Cloud',
                href: '/cloud-services/private-cloud',
                icon: 'lucide:cloud',
                description: 'Dedicated cloud infrastructure for business workloads',
              },
              {
                label: 'Colocation',
                href: '/cloud-services/data-centre-colocation',
                icon: 'lucide:server',
                description: 'Secure hosting within Australian data centres',
              },
              {
                label: 'Virtualisation',
                href: '/cloud-services/virtualization',
                icon: 'lucide:layers',
                description: 'Virtual servers using VMware or Hyper-V',
              },
              {
                label: 'Cloud Learning Management',
                href: '/cloud-services/cloud-learning-management',
                icon: 'lucide:book-open',
                description: 'Cloud-based learning and training platforms',
              },
            ],
          },
          {
            id: 'web-digital',
            title: 'Web & Digital',
            items: [
              {
                label: 'Website Design',
                href: '/web-solutions/website-design',
                icon: 'lucide:palette',
                description: 'Design and development of business websites',
              },
              {
                label: 'Domain Hosting',
                href: '/web-solutions/domain-hosting',
                icon: 'lucide:globe',
                description: 'Domain registration and DNS management',
              },
              {
                label: 'Email Hosting',
                href: '/web-solutions/email-hosting',
                icon: 'lucide:mail',
                description: 'Hosted email services for business use',
              },
              {
                label: 'Virtual Private Server',
                href: '/web-solutions/virtual-private-server',
                icon: 'lucide:server',
                description: 'Virtual servers with dedicated resources',
              },
              {
                label: 'Digital Branding',
                href: '/web-solutions/digital-branding',
                icon: 'lucide:sparkles',
                description: 'Brand identity and digital presence design',
              },
              {
                label: 'SSL Certificates',
                href: '/web-solutions/ssl-certificates',
                icon: 'lucide:lock',
                description: 'Website encryption and trust certificates',
              },
            ],
          },
        ],

        actions: {
          title: 'I need to...',
          links: [
            { label: 'Reduce IT downtime', href: '/contact?need=reduce-downtime' },
            { label: 'Outsource my IT', href: '/contact?need=outsource-it' },
            { label: 'Backup my business data', href: '/contact?need=backup' },
            { label: 'Plan IT for growth', href: '/contact?need=it-strategy' },
          ],
          featured: {
            label: 'Get a Security Audit',
            href: '/resources/cyber-security-checklist',
            description: 'Download our 2025 checklist',
          },
        },
      },

      // ============================================================
      // INDUSTRIES (2 product columns)
      // ============================================================
      {
        id: 'industries',
        label: 'Industries',
        hook: {
          label: 'Industries',
          headline: 'SECTOR EXPERTISE',
          description:
            'Tailored solutions for compliance-heavy and high-demand industries. We understand your challenges and speak your language.',
        },
        products: [
          {
            id: 'industries-list',
            title: 'Industries We Serve',
            items: [
              {
                label: 'Construction',
                href: '/industries/construction',
                icon: 'lucide:hard-hat',
                description: 'Site connectivity, lone worker, radio',
              },
              {
                label: 'Retail',
                href: '/industries/retail',
                icon: 'lucide:shopping-bag',
                description: 'VoIP, CCTV, cloud POS connectivity',
              },
              {
                label: 'Healthcare & Aged Care',
                href: '/industries/healthcare',
                icon: 'lucide:heart-pulse',
                description: 'Secure, reliable, compliant solutions',
              },
              {
                label: 'Logistics & Warehousing',
                href: '/industries/logistics',
                icon: 'lucide:warehouse',
                description: 'CCTV, WiFi, monitoring',
              },
            ],
          },
          {
            id: 'industries-list-2',
            title: '', // Empty title for visual continuation
            items: [
              {
                label: 'Professional Services',
                href: '/industries/professional-services',
                icon: 'lucide:briefcase',
                description: 'Teams Voice, IT support, cloud',
              },
              {
                label: 'Gyms & Fitness',
                href: '/industries/gyms-fitness',
                icon: 'lucide:dumbbell',
                description: 'Access control, CCTV, WiFi',
              },
              {
                label: 'Property Management',
                href: '/industries/property-management',
                icon: 'lucide:building-2',
                description: 'Intercoms, CCTV, monitoring',
              },
              {
                label: 'School Security',
                href: '/industry-security-solutions/school-security-systems',
                icon: 'lucide:school',
                description: 'CCTV, access, intercoms, PA, AV and cabling',
              },
            ],
          },
        ],
       actions: {
  title: 'Featured Projects',
  links: [
    { label: 'St John Ambulance NSW', href: '/about-us/our-projects/integrated-security-network-infrastructure-wifi-solution-st-john-ambulance-nsw' },
    { label: 'MCA Network Upgrade', href: '/about-us/our-projects/network-infrastructure-wi-fi-upgrade-mca' },
    { label: 'John Holland Tunnel', href: '/about-us/our-projects/mobile-signal-booster-john-holland-mascot-tunnel' },
  ],
  featured: {
    label: 'View All Projects',
    href: '/about-us/our-projects',
  },
},
      },

      // ============================================================
      // SUPPORT (2 product columns)
      // ============================================================
      {
        id: 'support',
        label: 'Support',
        hook: {
          label: 'Support',
          headline: "WE'RE HERE WHEN YOU NEED US",
          description:
            'Check out our handy guides and self-service tools. Our friendly, Australian-based team of business telco experts are a phone call away.',
          badge: '1300 300 210',
        },
        products: [
          {
            id: 'get-help',
            title: 'Get Help',
            items: [
              {
                label: 'Help Centre',
                href: '/help-centre',
                icon: 'lucide:book-open',
                description: 'Setup and troubleshooting guides',
              },
              {
                label: 'Contact Us',
                href: '/contact',
                icon: 'lucide:message-circle',
                description: 'Got a question? Get in touch',
              },
            ],
          },
          {
            id: 'tools',
            title: 'Tools',
            items: [
              {
                label: 'Remote Support',
                href: 'https://metwide1.screenconnect.com/',
                icon: 'lucide:monitor-smartphone',
                description: 'Quick remote assistance',
              },
            ],
          },
        ],
        actions: {
          title: 'Popular Topics',
          links: [
            { label: 'How to set up your modem', href: '/help-centre/broadband-nbn' },
            { label: 'VoIP troubleshooting', href: '/help-centre/voice' },
            { label: 'Email configuration', href: '/help-centre/managed-it' },
            { label: 'Alarms & monitoring help', href: '/help-centre/alarms-monitoring' },
          ],
        },
      },

      // ============================================================
      // ABOUT US (2 product columns)
      // ============================================================
      {
        id: 'about-us',
        label: 'About Us',
        hook: {
          label: 'About Metwide',
          headline: 'YOUR SINGLE PARTNER FOR INTEGRATED ICT',
          description:
            'Supporting Australian businesses with connectivity, security, and managed IT. One provider, complete accountability.',
          badge: 'ISO 9001 • ISO 14001 • ISO 45001',
        },
        products: [
          {
            id: 'company',
            title: 'Company',
            items: [
              {
                label: 'About Us',
                href: '/about-us',
                icon: 'lucide:building',
                description: 'Our story and values',
              },
              {
                label: 'Our Partners',
                href: '/about-us/our-partners',
                icon: 'lucide:handshake',
                description: 'Technology partnerships',
              },
              {
                label: 'Careers',
                href: '/about-us/careers',
                icon: 'lucide:users',
                description: 'Join our team',
              },
            ],
          },
          {
            id: 'resources',
            title: 'Resources',
            items: [
              {
                label: 'Blog & News',
                href: '/blog',
                icon: 'lucide:newspaper',
                description: 'Industry insights and updates',
              },
              {
  label: 'Our Clients',
  href: '/about-us/clients',
  icon: 'lucide:heart-handshake',
  description: 'Trusted business relationships',
},
              {
                label: 'Our Projects',
                href: '/about-us/our-projects',
                icon: 'lucide:layout-grid',
                description: 'Featured implementations',
              },
            ],
          },
        ],
        actions: {
          title: 'Get Started',
          links: [
            { label: 'Request a Quote', href: '/quote' },
            { label: 'Book a Consultation', href: '/contact?type=consultation' },
            { label: 'Schedule a Demo', href: '/contact?type=demo' },
          ],
          featured: {
            label: 'View Industries We Serve',
            href: '/industries',
          },
        },
      },
    ],
    get phone() {
      return {
        label: display.value,
        href: `tel:${tel.value}`,
      }
    },
    cta: {
      label: 'Get a Free Quote',
      href: '/quote',
    },
  }
}
